import type { ReportConfiguration } from '../report-configurations/reportConfigurationsTypes';
import type * as types from './generatedReportsTypes';

export function transformResponseToReport(
  response: types.TransformResponse,
  reportConfiguration: ReportConfiguration,
): types.Report {
  const data = response.data.resources ? response.data.resources[0] : response.data;

  // Read from the request configuration, not `data.reportType`: the response field falls back to
  // 'TRANSACTION' below when QCS omits it, which would make a comparison report claim to be a
  // transaction report and take the legacy code paths guarded here.
  const isComparison = reportConfiguration.reportType === 'COMPARISON';

  const rows: types.ReportRow[] = (data.rows || []).map((row: types.ReportRow) =>
    transformResponseRowToReportRow(row, '', reportConfiguration),
  );
  let columns: types.ReportColumn[] = (data.columns || []).map((column: types.ReportColumn) =>
    transformResponseColumnToReportColumn(column),
  );

  // TODO: This should be removed when QCS fixes issue with outputting a total column or cellData for total
  //       column when there is only a single column of report data.
  //
  // Never applies to COMPARISON: the smallest comparison report is one VALUE column plus its group
  // TOTAL, which matches this shape exactly and would lose a real column and a cell from every row.
  if (!isComparison && columns.length === 2 && columns[1].isAggSum) {
    columns = columns.slice(0, -1);
    rows.forEach((row) => {
      if (row.cellData.length === 2) {
        row.cellData = row.cellData.slice(0, -1);
      }
    });
  }

  return {
    configuration: reportConfiguration,
    name: reportConfiguration.name || 'unknown',

    reportType: data.reportType || 'TRANSACTION',
    reportSubType: data.reportSubType,
    persistentType: data.persistentType,

    startOn: data.startOn || '',
    endOn: data.endOn || '',

    total: data.total,

    rowType: data.rowType,
    rows,

    columnType: data.columnType,
    columns,
  };
}

function transformResponseColumnToReportColumn(column: types.ReportColumn): types.ReportColumn {
  let headerValue: types.ReportColumn['headerValue'];
  switch (column.headerType) {
    case 'DATE_RANGE':
      // Without `name` every comparison range fell back to its group label and read "Total" (SIMPL-34995).
      headerValue = {
        startOn: column.headerValue.startOn,
        endOn: column.headerValue.endOn,
        name: column.headerValue.name,
      };
      break;
    case 'QCS_ENTITY':
      headerValue = {
        id: column.headerValue.id,
        qcs_entity: column.headerValue.qcs_entity,
        name: column.headerValue.name,
      };
      break;
    case 'STRING':
    default:
      headerValue = { id: column.headerValue.id, string: column.headerValue.string };
  }

  // This is a whitelist followed by an `as` cast, so anything not listed here is dropped silently —
  // no type error, no runtime error. Any new server column field must be added explicitly.
  return {
    id: column.id,
    headerType: column.headerType,
    headerValue,
    cellType: column.cellType,
    isAggSum: column.isAggSum,

    columnGroupId: column.columnGroupId,
    columnGroupLabel: column.columnGroupLabel,
    columnGroupType: column.columnGroupType,
    rangeId: column.rangeId,
    rangeLabel: column.rangeLabel,
    comparisonColumnType: column.comparisonColumnType,
  } as types.ReportColumn;
}

function transformResponseRowToReportRow(
  row: types.ReportRow,
  idPrefix: string,
  reportConfiguration: ReportConfiguration,
): types.ReportRow {
  // Personal Tax Reports need to be bumped up 1 level.
  // Comparison rows are unioned on `entityId` and must never go through this id arithmetic; the
  // `reportType === 'TRANSACTION'` term already excludes them, but state it so the intent survives.
  const { reportSubType, reportType, rowType } = reportConfiguration;
  const isPeronsalTaxReport = reportType === 'TRANSACTION' && reportSubType === 'TAX_SUMMARY' && rowType === 'USAGE';
  const { rowId, rowParentId } = isPeronsalTaxReport
    ? {
        rowId: (Number(row.id) - 1).toString(),
        rowParentId: (Number(row.parentId) - 1).toString(),
      }
    : {
        rowId: row.id,
        rowParentId: row.parentId,
      };

  return {
    id: `${idPrefix}${rowId}`,
    parentId: row.parentId !== undefined ? `${idPrefix}${rowParentId}` : undefined,
    name: `${idPrefix}${row.name}`,
    secondaryText: row.secondaryText,
    featurePath: row.featurePath,
    entityId: row.entityId,
    entityType: row.entityType,
    childrenEntityIds: row.childrenEntityIds,
    expectedSign: row.expectedSign,
    isExcludedFromReports: row.isExcludedFromReports,
    cellData: row.cellData.map((cell: types.ReportCell) => ({
      id: cell.id,
      columnId: cell.columnId,
      startValue: cell?.startValue,
      value: cell.value,
      ...(cell.items ? { items: cell.items } : undefined),
      // Comparison derived values. Keyed on `!== undefined` rather than truthiness so that a
      // `deltaPercentage` of `null` — the comparison value was zero, so there is no ratio — is
      // preserved as null and stays distinguishable from the field being absent entirely.
      ...(cell.deltaAmount !== undefined ? { deltaAmount: cell.deltaAmount } : undefined),
      ...(cell.deltaPercentage !== undefined ? { deltaPercentage: cell.deltaPercentage } : undefined),
    })),
    other: row.other,
    deltaAmount: row.deltaAmount,
    deltaPercentage: row.deltaPercentage,
  };
}
